import React, { useState } from 'react';
import { Organization, useOrganization } from '../../contexts/OrganizationContext';
import { Checkbox, Spinner } from '../../design-system';
import toast from '../../design-system/toast';

interface OrgFindingClosureSettingProps {
  organization: Organization;
}

const DESCRIPTION_ID = 'org-finding-closure-reason-description';

/**
 * Owner-only setting: require a reason whenever a finding is closed without a fix.
 * Everyone else sees the control disabled, so the policy stays visible to them.
 */
const OrgFindingClosureSetting: React.FC<OrgFindingClosureSettingProps> = ({ organization }) => {
  const { updateOrganization } = useOrganization();
  // The value being saved, shown while the request is in flight.
  const [savingValue, setSavingValue] = useState<boolean | null>(null);

  // Read the role from the displayed organization: the settings page shows the one in
  // the URL, which is not always the current organization.
  const isOwner = organization.current_user_role === 'owner';
  const isSaving = savingValue !== null;
  const checked = savingValue ?? Boolean(organization.require_finding_closure_reason);

  const handleChange = async (next: boolean) => {
    if (!isOwner || isSaving) return;

    setSavingValue(next);
    try {
      // updateOrganization merges the response into context. Do not follow it with
      // refreshOrganizations(): that puts the whole page behind a loading spinner.
      await updateOrganization(organization.id, { require_finding_closure_reason: next });
      toast.success(
        next ? 'A reason is now required to close a finding' : 'A reason is no longer required to close a finding',
      );
    } catch (err: any) {
      console.error('Failed to update finding closure setting:', err);
      toast.error(err?.message || 'Failed to update this setting');
    } finally {
      setSavingValue(null);
    }
  };

  return (
    <div className="bg-ui-background-tertiary rounded-lg border border-ui-border-primary p-6">
      <h4 className="text-sm font-medium text-ui-text-secondary mb-3">Closing findings</h4>
      <div className="flex items-center gap-2">
        <Checkbox
          checked={checked}
          onChange={(event) => handleChange(event.target.checked)}
          disabled={!isOwner || isSaving}
          aria-describedby={DESCRIPTION_ID}
          label={<span className="text-ui-text-primary">Require a reason to close a finding without a fix</span>}
        />
        {isSaving && <Spinner size={14} tone="muted" label="Saving" />}
      </div>
      <p id={DESCRIPTION_ID} className="mt-2 text-xs text-ui-text-tertiary">
        Anyone marking a finding as Resolved, Ignored or False Positive must say why, in the app and through the
        API, CLI and MCP server. The reason is saved in the finding's history. Findings resolved by a merged pull
        request are not affected.
      </p>
      {!isOwner && (
        <p className="mt-2 text-xs text-ui-text-tertiary">Only the organization owner can change this setting.</p>
      )}
    </div>
  );
};

export default OrgFindingClosureSetting;
